import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, Leaf, Landmark, Building, GitMerge, ArrowRight } from 'lucide-react';
import SEO from '@/components/SEO';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const ServicesPage = () => {
  const { locale } = useParams();

  const services = [
    { 
      title: 'Pharma Engineering & PMC', 
      description: 'End-to-end Project Management Consultancy (PMC) services for pharmaceutical, nutraceutical, API, biotech, and healthcare manufacturers. 25+ years of hands-on experience.',
      path: '/services/pharma-engineering',
      icon: Building2,
      highlights: ['cGMP-compliant facility expansion', 'HVAC & utility systems', 'Industrial automation & audits']
    },
    { 
      title: 'Sustainability & ESG / BRSR', 
      description: 'SEBI-mandated BRSR report generation and comprehensive ESG strategy implementation for India\'s top 1,000 listed companies. Transform regulatory compliance into competitive advantage.',
      path: '/services/sustainability',
      icon: Leaf,
      highlights: ['SEBI BRSR compliance', 'ESG strategy development', 'GRI/SASB/TCFD frameworks']
    },
    { 
      title: 'Finance & Loans', 
      description: 'Capital structuring, financial audits, and loan facilitation for enterprises expanding capacity or acquiring assets. Expert guidance through complex financial decisions.',
      path: '/services/finance-loans',
      icon: Landmark,
      highlights: ['Project finance structuring', 'Financial due diligence', 'Loan facilitation']
    },
    { 
      title: 'Capital Markets', 
      description: 'Full-service real estate capital markets advisory including investment sales, debt & structured finance, equity advisory, and valuation services across all commercial asset classes.',
      path: '/services/capital-markets',
      icon: Building,
      highlights: ['Investment sales', 'Debt & structured finance', 'Equity advisory & valuation']
    },
    { 
      title: 'Mergers & Acquisitions', 
      description: 'Full-cycle M&A advisory — from identifying strategic targets to closing deals and managing integration. Financial rigor meets industry-specific diligence.',
      path: '/services/mergers-acquisitions',
      icon: GitMerge,
      highlights: ['Deal origination', 'Due diligence', 'Post-merger integration']
    },
  ];

  return (
    <div className="pt-20" data-testid="services-page">
      {/* Hero */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-white via-light-grey to-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-teal font-medium text-sm uppercase tracking-wider">Our Expertise</span>
            <h1 className="font-playfair text-4xl md:text-5xl lg:text-6xl text-navy mt-4 mb-6">
              Five Practice Areas. One Vision.
            </h1>
            <p className="text-lg text-mid-grey">
              H&G delivers specialized expertise across engineering, sustainability, finance, capital markets, and M&A — 
              each practice area staffed by industry veterans with decades of Fortune 500 experience.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="space-y-12"
          >
            {services.map((service, index) => {
              const IconComponent = service.icon;
              const isEven = index % 2 === 0;
              
              return (
                <motion.div
                  key={service.path}
                  variants={fadeInUp}
                  className={`grid lg:grid-cols-2 gap-12 items-center ${!isEven ? 'lg:flex-row-reverse' : ''}`}
                >
                  <div className={isEven ? 'lg:order-1' : 'lg:order-2'}>
                    <div className="w-16 h-16 bg-teal/10 rounded-2xl flex items-center justify-center mb-6">
                      <IconComponent className="w-8 h-8 text-teal" />
                    </div>
                    <h2 className="font-playfair text-3xl text-navy mb-4">{service.title}</h2>
                    <p className="text-mid-grey leading-relaxed mb-6">{service.description}</p>
                    
                    <ul className="space-y-2 mb-8">
                      {service.highlights.map((highlight) => (
                        <li key={highlight} className="flex items-center text-sm text-navy">
                          <span className="w-2 h-2 bg-teal rounded-full mr-3" />
                          {highlight}
                        </li>
                      ))}
                    </ul>
                    
                    <Link 
                      to={`/${locale}${service.path}`}
                      className="inline-flex items-center text-teal font-medium hover:text-teal transition-colors"
                      data-testid={`service-link-${service.path.split('/').pop()}`}
                    >
                      Explore {service.title.split(' ')[0]} Services <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                  </div>
                  
                  <div className={`bg-light-grey rounded-2xl p-8 h-64 flex items-center justify-center ${isEven ? 'lg:order-2' : 'lg:order-1'}`}>
                    <IconComponent className="w-32 h-32 text-teal/20" />
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-teal">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-playfair text-4xl text-white mb-6">
            Not Sure Which Service You Need?
          </h2>
          <p className="text-white/80 text-lg mb-8">
            Our advisors can help identify the right approach for your specific challenge.
          </p>
          <Link to={`/${locale}/contact`} className="btn-primary">
            Schedule a Discovery Call
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
